import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Flex, Grid, Pagination } from '@highereducation/hen-ui';
import { useDataSourceManager } from '@de/providers/DataSourceManager';
import {
  ItemScopeProvider,
  useItemScopeIndexPath,
} from '@de/providers/ItemScopeContext';
import { useActionDispatcher } from '@de/utils/action-resolver/ActionDispatcher';
import { uuid } from '@de/shared/utils';
import { useTracking } from '@de/providers/TrackingProvider';
import { composeFlowElementKey } from '@de/utils/composeHostElementKey';
import { interpolateObject } from '@de/utils/interpolation';
import type { DataSourceDeclaration } from '@de/providers/DataSourceManager.types';
import { isHttpDataSourceDeclaration } from '@de/utils/data-sources/data-source-kind';
import { extractIteratorArray } from './extractIteratorArray';
import {
  paginationFormKeys,
  paginationFormStateEntries,
  readResetFormKeys,
  resolveServerPagination,
} from './resolveServerPagination';
import { rankItemsByTags, parseActiveFilterKeys } from './rankItemsByTags';
import { usePagination, type PaginationMode } from './usePagination';
import { useSort, type SortOption } from './useSort';
import { BoundStatusShell } from '../BoundStatusShell';
import type { BoundStatusInput } from '../../utils/boundStatus/resolveBoundStatus';
import { LoadingCardSkeleton, LoadingSkeleton } from './states/LoadingSkeleton';
import { warnOnce } from '@de/utils/warnOnce';
import { EmptyState } from './states/EmptyState';
import { ErrorState } from './states/ErrorState';

/**
 * Empty array singletons let `useSort` / `usePagination` skip the
 * "items reference changed" reset effect when the author leaves the
 * corresponding option off.
 */
const EMPTY_OPTIONS: ReadonlyArray<SortOption> = [];
export const DEFAULT_REQUESTED_ITEM_LIMIT = 12;
const MIN_REQUESTED_ITEM_LIMIT = 1;
const MAX_REQUESTED_ITEM_LIMIT = 100;

export type ResolvedRequestedItemLimit = {
  /** Skeleton count. An authored request limit does not cap rendered rows. */
  readonly count: number;
  /** True when the request body carried a whole-number `limit`. */
  readonly authored: boolean;
};

// Generic container segment with no section meaning (a bare `data` path iterates
// the root array, not a named bucket).
const GENERIC_ITERATOR_ROOT = 'data';

// Section/bucket name from a repeater's `iteratorPath` (the leading dot-path
// segment, e.g. `recommended.data` → `'recommended'`), used to refine the card
// `location` per section. `undefined` for an empty, single-segment, or generic
// `data` path. Exported for unit coverage.
export function sectionFromIteratorPath(
  iteratorPath: string | undefined,
): string | undefined {
  if (!iteratorPath) return undefined;
  const trimmed = iteratorPath.trim();
  // A named bucket has a `<bucket>.data` sub-path; a single segment does not.
  if (!trimmed.includes('.')) return undefined;
  const first = trimmed.split('.')[0]?.trim();
  if (!first || first === GENERIC_ITERATOR_ROOT) return undefined;
  return first;
}

export function resolveRequestedItemLimit(
  declaration: DataSourceDeclaration | undefined,
  interpolationContext: unknown,
): ResolvedRequestedItemLimit {
  const fallback: ResolvedRequestedItemLimit = {
    count: DEFAULT_REQUESTED_ITEM_LIMIT,
    authored: false,
  };
  if (!declaration || !isHttpDataSourceDeclaration(declaration)) {
    return fallback;
  }

  try {
    const resolvedBody = interpolateObject(
      declaration.body,
      interpolationContext,
    );
    const limit = wholeNumberLimit(parseRequestBody(resolvedBody)?.limit);
    if (limit === undefined) return fallback;
    return {
      count: Math.min(
        MAX_REQUESTED_ITEM_LIMIT,
        Math.max(MIN_REQUESTED_ITEM_LIMIT, limit),
      ),
      authored: true,
    };
  } catch {
    return fallback;
  }
}

function wholeNumberLimit(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isInteger(value)) return value;
  if (typeof value === 'string' && /^-?\d+$/.test(value.trim())) {
    return Number(value.trim());
  }
  return undefined;
}

function parseRequestBody(
  value: unknown,
): Readonly<Record<string, unknown>> | undefined {
  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    return value as Readonly<Record<string, unknown>>;
  }
  if (typeof value !== 'string') {
    return undefined;
  }
  try {
    const parsed = JSON.parse(value) as unknown;
    return parsed !== null &&
      typeof parsed === 'object' &&
      !Array.isArray(parsed)
      ? (parsed as Readonly<Record<string, unknown>>)
      : undefined;
  } catch {
    return undefined;
  }
}

/** Label for the View More CTA — legacy Supersonic EL parity. */
const VIEW_MORE_LABEL = 'View More Programs';

/**
 * Forward action dispatched once every fetched row is visible. Hardcoded
 * to match the legacy EL "View More Programs" CTA, which advanced the
 * flow — the same `navigation.advanceStep` handler the step's other
 * forward CTAs use.
 */
const VIEW_MORE_FORWARD_ACTION = 'navigation.advanceStep';

/**
 * Module-level store of per-repeater "View More revealed" flags, keyed
 * by a stable, DSL-derived identity
 * (`<flowSessionId>::<dslConfigId>::<stepIndex>::<puckNodeId>`).
 *
 * Why module-level and not `useState` alone: in the production /
 * dev-harness render path, ancestor re-renders driven by DSM
 * `formState` and `sources` updates routinely tear down and re-create
 * the entire bound subtree (see the identical reasoning on
 * `ON_SOURCE_CHANGE_SETTLED_SNAPSHOTS` in `createBoundRender`). A
 * per-instance `useState` is therefore re-initialised to `false` on
 * every such remount — so the moment a user touches a filter
 * `ToggleGroup` (which writes `formState`), an already-expanded View
 * More window collapses back to `limit` rows. Pinning the reveal flag
 * to a remount-surviving key keeps the window open.
 *
 * The stored `itemsRef` doubles as the re-fetch signal: it holds the
 * raw extracted source array at the time the flag was written. When the
 * underlying source re-fetches, `allItems` becomes a new reference and
 * the window legitimately resets — filter re-ranking never changes
 * `allItems`, so it never resets here.
 */
const VIEW_MORE_REVEAL = new Map<
  string,
  { readonly itemsRef: unknown; readonly expanded: boolean }
>();

/**
 * Test-only escape hatch for clearing the module-scoped reveal store.
 * Production callers never invoke this — the flag survives remounts on
 * purpose. Mirrors `resetOnSourceChangeSnapshots` in `createBoundRender`.
 */
export function resetViewMoreRevealStore(): void {
  VIEW_MORE_REVEAL.clear();
}

/**
 * Footer mode for the repeater.
 *
 * - `'none'` — no pagination UI; render every fetched row.
 * - `'server'` — render the fetched page and page via `formState`.
 * - `'paginated'` / `'loadMore'` — client windowing via {@link usePagination}.
 * - `'viewMore'` — render `limit` rows behind a single repeater-owned
 *   "View More Programs" CTA (legacy EL parity): the first click reveals
 *   the remaining fetched rows; once everything is visible — or the
 *   response never exceeded `limit` — the default `'advance'` behavior
 *   dispatches `navigation.advanceStep`, while `'hide'` removes the CTA.
 *
 * The admin editor authors `'none'` and strips it on save, so an omitted
 * `paginationMode` must also mean "no pagination" — not `'paginated'`.
 */
export type RepeaterPaginationMode =
  | PaginationMode
  | 'viewMore'
  | 'none'
  | 'server';

type ViewMoreOnExhausted = 'advance' | 'hide';

function resolveViewMoreOnExhausted(value: unknown): ViewMoreOnExhausted {
  return value === 'hide' ? 'hide' : 'advance';
}

const PAGINATION_MODES = new Set<RepeaterPaginationMode>([
  'none',
  'paginated',
  'loadMore',
  'viewMore',
  'server',
]);

/**
 * Explicit modes win. Omitted mode (editor strips `'none'`) means no
 * pagination unless a legacy `pageSize` alone is present.
 */
export function resolvePaginationMode(
  paginationMode: RepeaterPaginationMode | undefined,
  pageSize: number | undefined,
): RepeaterPaginationMode {
  if (paginationMode && PAGINATION_MODES.has(paginationMode)) {
    return paginationMode;
  }
  if (typeof pageSize === 'number' && pageSize > 0) return 'paginated';
  return 'none';
}

export type DataRepeaterLayout = 'flex' | 'grid';
/**
 * Layout prop unions are written out explicitly (not indexed off hen-ui
 * `FlexProps` / `GridProps`). Exporting `DataRepeater` through the package
 * entry with those indexed types pulls hen-ui's CVA `as const` variant maps
 * into vite-plugin-dts `rollupTypes` / api-extractor, which crashes with
 * `Unable to follow symbol for "const"`.
 */
export type DataRepeaterFlexDirection = 'row' | 'column';
export type DataRepeaterFlexWrap = 'wrap' | 'nowrap';
export type DataRepeaterFlexAlign = 'start' | 'center' | 'end' | 'stretch';
export type DataRepeaterFlexJustify = 'start' | 'center' | 'end' | 'between';
export type DataRepeaterGridColumns = 1 | 2 | 3 | 4 | 5 | 6;
/** Intersection of Flex and Grid gap tokens used by the repeater layout. */
export type DataRepeaterGap =
  | 'xxs'
  | 'xs'
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl'
  | 'max';
export type DataRepeaterLoadingVariant = 'rows' | 'card';

export interface DataRepeaterProps {
  /**
   * Puck node id for this repeater (passed through from the saved DSL
   * tree by the `platform.dataRepeater` render). Used only to key the
   * remount-surviving View More reveal store — see
   * {@link VIEW_MORE_REVEAL}. Absent in ad-hoc/legacy mounts, in which
   * case the reveal flag falls back to plain per-instance state.
   */
  readonly id?: string;
  /** Name of the `dataSources[…]` entry to iterate. */
  readonly source: string;
  /**
   * Plain dot path applied to the resolved source data. Empty / omitted
   * means "iterate `data` itself". `[*]` wildcards are rejected — see
   * {@link extractIteratorArray}.
   */
  readonly iteratorPath?: string;
  /** Number of rows per page. Falsy disables pagination entirely. */
  readonly pageSize?: number;
  readonly paginationMode?: RepeaterPaginationMode;
  /**
   * `server` mode. Dot path for the result total on this repeater's
   * source. Empty uses `pagination.total`.
   */
  readonly paginationTotal?: string;
  /**
   * `server` mode. Page size. Offset starts at 0 and a click advances
   * it by this amount. Not read from the response.
   */
  readonly paginationLimit?: number | string;
  /**
   * `server` mode. Required prefix for the form-state keys the pager
   * writes. Empty means the repeater does not publish to form state.
   */
  readonly paginationFormKey?: string;
  /**
   * `server` mode. `top` sits above the rows, `bottom` below them,
   * and `both` renders a pager in each place. Omitted is `bottom`.
   */
  readonly paginationPosition?: 'top' | 'bottom' | 'both';
  /** `server` mode. Horizontal alignment of the pager. */
  readonly paginationAlign?: 'left' | 'center' | 'right';
  /**
   * `server` mode. When one of these form-state keys changes, the
   * offset key is written back to `"0"`.
   */
  readonly resetWhenFormKeysChange?: readonly string[] | string;
  /**
   * `viewMore` mode only — initial number of rows to render before the
   * View More CTA. Falsy renders every fetched row (the CTA then goes
   * straight to the forward action). Distinct from `pageSize`, which
   * drives the `paginated` / `loadMore` modes.
   */
  readonly limit?: number;
  /**
   * `viewMore` mode only — behavior after every fetched row is visible.
   * `'hide'` removes the CTA; `'advance'` dispatches the legacy forward
   * action when clicked. Omitted or unrecognized values use `'advance'`.
   */
  readonly viewMoreOnExhausted?: ViewMoreOnExhausted;
  /**
   * Enable client-side tag-driven re-ranking (the "best for you"
   * behavior of the legacy Super Sonic EL). When `true`, the repeater
   * reads the active filter keys from `formState[filterStateKey]` (a
   * comma-separated list, written by a filter `ToggleGroup` whose
   * `formKey` matches) and re-orders rows so those matching more keys
   * (affirmative at `filterPath`) float to the top — **nothing is
   * hidden**, non-matches just sink below. Off (default) = original
   * order. The rank runs before sort + pagination, so the best matches
   * surface within the View More window.
   */
  readonly filter?: boolean;
  /**
   * `filter` mode — the `formState` key holding the active filter keys.
   * Must match the filter `ToggleGroup`'s `formKey`. Defaults to
   * `'activeFilters'`.
   */
  readonly filterStateKey?: string;
  /**
   * `filter` mode — dot path on each row to the `{ key: { value } }`
   * stats object the filter keys are checked against (the same map the
   * `tagLabels` transform reads). Defaults to `'institutionalStats'`.
   */
  readonly filterPath?: string;
  /** Render the sort dropdown when true and `sortOptions` is non-empty. */
  readonly sortable?: boolean;
  /** Author-supplied sort choices — see {@link SortOption}. */
  readonly sortOptions?: ReadonlyArray<SortOption>;
  /** Row layout for resolved items. Defaults to the historical flex column list. */
  readonly layout?: DataRepeaterLayout;
  /** Flex direction for repeated items. Used only when `layout="flex"`. Defaults to `column`. */
  readonly direction?: DataRepeaterFlexDirection;
  /** Flex wrapping behavior. Used only when `layout="flex"`. Defaults to `nowrap`. */
  readonly wrap?: DataRepeaterFlexWrap;
  /** Flex cross-axis alignment. Used only when `layout="flex"`. Defaults to `stretch`. */
  readonly align?: DataRepeaterFlexAlign;
  /** Flex main-axis alignment. Used only when `layout="flex"`. Defaults to `start`. */
  readonly justify?: DataRepeaterFlexJustify;
  /** Number of grid columns. Used only when `layout="grid"`. Defaults to 1. */
  readonly columns?: DataRepeaterGridColumns;
  /** Responsive grid columns at the HEN UI `md` breakpoint. */
  readonly columnsMd?: DataRepeaterGridColumns;
  /** Responsive grid columns at the HEN UI `lg` breakpoint. */
  readonly columnsLg?: DataRepeaterGridColumns;
  /** Responsive grid columns at the HEN UI `xl` breakpoint. */
  readonly columnsXl?: DataRepeaterGridColumns;
  /**
   * Minimum column size, such as `322px`. Used only when `layout="grid"`.
   * The row scrolls horizontally and column counts are ignored. The product
   * card preset `322px` renders as a 300px floor with the leftover split equally.
   */
  readonly minColumnWidth?: string;
  /** HEN UI spacing token between rendered items. Defaults to historical 16px spacing. */
  readonly gap?: DataRepeaterGap;
  /** Body copy for the empty state. Heading defaults to "No results". */
  readonly emptyStateText?: string;
  /** Heading for the error state. Defaults to "Couldn't load results". */
  readonly errorStateHeading?: string;
  /** Body copy for the error state. */
  readonly errorStateBody?: string;
  /** Optional retry CTA label. Renders only when `onRetry` is also supplied. */
  readonly errorCtaLabel?: string;
  /**
   * Optional retry handler. Wired by hosts (or `_action.onRetry`) when
   * a real refetch is available; absent today because no
   * `dataSource.retry` action handler ships in the dispatcher.
   */
  readonly onRetry?: () => void;
  /**
   * Adapter failure forwarded by the binding enhancer. The shell shows its
   * error surface for this instead of an empty list: the fetch returned rows
   * the repeater could not read.
   */
  readonly adapterError?: BoundStatusInput['adapterError'];
  /**
   * The per-row template. Authored as a Puck `slot` so each child node
   * passes through the binding enhancer; rendered once per row inside
   * an {@link ItemScopeProvider}, so binding-resolver lookups against
   * `_bind: { source: 'item', path: '…' }` resolve to the row data.
   *
   * When `repeatInsideShell` is true, this is instead one shell (a
   * carousel) holding exactly one child. The shell is rendered once and
   * that child is the per-row template.
   */
  readonly children?: React.ReactNode;
  /**
   * When `true`, render the single shell passed as `children` once and
   * repeat its only child once per row inside it. Omitted and `false`
   * keep the historical path: clone the whole template once per row.
   */
  readonly repeatInsideShell?: boolean;
  /**
   * Use card-shaped placeholders while the source is pending. The DSL
   * renderer derives this from the repeated template; authors do not manage
   * a second loading-state setting.
   */
  readonly loadingVariant?: DataRepeaterLoadingVariant;
}

/**
 * Derive a stable React key for a repeater row from its content identity
 * rather than its list slot, mirroring the imperative `ResultList` key
 * (`${programId}-${schoolName}-${programName}`). Now that correlation ids
 * are minted per `RepeaterRow` instance (`useRef`), keying by slot index
 * would rebind an instance's ids to a different offering whenever a row is
 * reordered/filtered/replaced at a position (sort, tag-ranking, or
 * paginate-by-replace). A content key keeps the instance — and its ids —
 * bound to the offering. Falls back to the slot `index` for rows with no
 * derivable identity (non-result iterables) so keys stay unique.
 */
const deriveRowKey = (item: unknown, index: number): React.Key => {
  if (item && typeof item === 'object') {
    const row = item as Record<string, unknown>;
    const parts = [row.programId, row.schoolName, row.programName].filter(
      (part): part is string | number =>
        typeof part === 'string' || typeof part === 'number',
    );
    if (parts.length > 0) return parts.join('-');
  }
  return index;
};

/**
 * Per-row wrapper that mints instance-scoped correlation ids and stamps
 * them onto the row before it enters the item scope. The DSL `Trackable`
 * path reads `viewCorrelationId` / `productClickedCorrelationId` off the
 * row via `buildProductIdentityFromItem`, so minting here — once per
 * rendered row instance (`useRef`, stable for the instance's lifetime) —
 * scopes correlation to the card instance rather than the shared data
 * row. Two repeaters over the same offering render two `RepeaterRow`
 * instances and therefore get distinct ids.
 */
const RepeaterRow: React.FC<{
  item: unknown;
  index: number;
  section?: string;
  children: React.ReactNode;
}> = ({ item, index, section, children }) => {
  const viewCorrelationId = useRef(uuid()).current;
  const productClickedCorrelationId = useRef(uuid()).current;
  const itemWithCorrelation = useMemo(
    () =>
      item && typeof item === 'object'
        ? {
            ...(item as Record<string, unknown>),
            viewCorrelationId,
            productClickedCorrelationId,
          }
        : item,
    [item, viewCorrelationId, productClickedCorrelationId],
  );
  return (
    <ItemScopeProvider
      item={itemWithCorrelation}
      index={index}
      section={section}
    >
      {children}
    </ItemScopeProvider>
  );
};

const SHELL_WARNING_NAMESPACE = 'platform.dataRepeater';
const SHELL_SHAPE_WARNING =
  '[platform.dataRepeater] repeatInsideShell requires one shell holding exactly one child template.';

function isCarouselShell(shell: React.ReactElement): boolean {
  const authored = (shell.type as { authoredDslType?: unknown })
    .authoredDslType;
  if (authored === 'Carousel') return true;
  return componentTypeName(shell.type) === 'Carousel';
}

function componentTypeName(
  type: React.ReactElement['type'],
): string | undefined {
  if (typeof type === 'string') return type;
  if (typeof type === 'function') {
    const named = type as { displayName?: string; name?: string };
    return named.displayName || named.name;
  }
  if (typeof type === 'object' && type !== null) {
    const named = type as {
      displayName?: string;
      render?: { displayName?: string; name?: string };
    };
    return named.displayName || named.render?.displayName || named.render?.name;
  }
  return undefined;
}

/**
 * The DSL renderer used to wrap each authored child in a Fragment. Flatten
 * that wrapper so the cloned template is the slide itself.
 */
function unwrapSingleFragment(element: React.ReactElement): React.ReactElement {
  if (element.type !== React.Fragment) return element;
  const inner = React.Children.toArray(
    (element.props as { children?: React.ReactNode }).children,
  ).filter(React.isValidElement);
  return inner.length === 1 ? inner[0] : element;
}

function readSingleShellTemplate(children: React.ReactNode): {
  shell?: React.ReactElement;
  template?: React.ReactElement;
} {
  const shells = React.Children.toArray(children).filter(React.isValidElement);
  const shell = shells.length === 1 ? shells[0] : undefined;
  const templates = shell
    ? React.Children.toArray(
        (shell.props as { children?: React.ReactNode }).children,
      ).filter(React.isValidElement)
    : [];
  if (!shell || templates.length !== 1) {
    warnOnce(SHELL_WARNING_NAMESPACE, SHELL_SHAPE_WARNING);
    return {};
  }
  return { shell, template: unwrapSingleFragment(templates[0]) };
}

/**
 * Render the single shell once and repeat its only child once per row
 * inside it. The repeater does not add Flex or Grid, because the shell
 * (for example a carousel) owns the layout of its direct children.
 */
function repeatInsideShellChild(
  children: React.ReactNode,
  rows: readonly unknown[],
  indexOffset: number,
  section: string | undefined,
): React.ReactNode {
  const { shell, template } = readSingleShellTemplate(children);
  if (!shell || !template) return null;
  const repeated = rows.map((row, i) => {
    const absoluteIndex = indexOffset + i;
    return (
      <RepeaterRow
        key={deriveRowKey(row, absoluteIndex)}
        item={row}
        index={absoluteIndex}
        section={section}
      >
        {template}
      </RepeaterRow>
    );
  });
  return React.cloneElement(shell, undefined, repeated);
}

function loadingInsideShellChild(
  children: React.ReactNode,
  count: number,
): React.ReactNode {
  const { shell, template } = readSingleShellTemplate(children);
  if (!shell || !template) return null;

  const placeholders = Array.from({ length: count }, (_, index) =>
    React.cloneElement(
      template as React.ReactElement<{ children?: React.ReactNode }>,
      { key: `loading-${index}` },
      <LoadingCardSkeleton />,
    ),
  );
  const loadingShellProps: Record<string, unknown> = {};
  if (isCarouselShell(shell)) {
    const shellProps = shell.props as {
      showControls?: unknown;
      showDots?: unknown;
    };
    if (shellProps.showControls === undefined) {
      loadingShellProps.showControls = false;
    }
    if (shellProps.showDots === undefined) {
      loadingShellProps.showDots = false;
    }
  }
  return React.cloneElement(shell, loadingShellProps, placeholders);
}

/**
 * Iterate a resolved data source and render `children` per row.
 *
 * Lifecycle (SAD §10 / ADR-13):
 *   1. Read the source via DSM. Pending or missing → loading skeleton.
 *   2. Extract the iteration array via `iteratorPath`. Non-array →
 *      empty state.
 *   3. Apply optional sort + pagination (both no-op when disabled).
 *   4. `error` status → ErrorState with author-supplied / generic copy.
 *   5. Empty visible window → EmptyState.
 *   6. Otherwise render `children` once per row, each wrapped in
 *      {@link ItemScopeProvider}.
 *
 * Hooks order: every hook is called unconditionally at the top so the
 * status branches can return early without violating the Rules of
 * Hooks. The two memoized arrays (`items`, `sortOptions`) keep the
 * downstream resets stable across renders.
 *
 * Accessibility: rows render inside a `region` landmark with
 * `aria-live="polite"` so screen readers announce page / sort
 * transitions. Focus is intentionally NOT moved on page change
 * (open question Q4 in plan) — `aria-live` is sufficient and avoids
 * yanking focus mid-task.
 */

/** Saved product-card rows store this length. The track floor is 300px. */
const PRODUCT_CARD_COLUMN_WIDTH = '322px';
const PRODUCT_CARD_COLUMN_MIN = '300px';
const PRODUCT_CARD_COLUMN_MAX = '1fr';

function scrollingColumnProps(minColumnWidth: string | undefined): {
  minColumnWidth?: string;
  maxColumnWidth?: string;
} {
  const width = minColumnWidth?.trim();
  if (!width) return {};
  if (width === PRODUCT_CARD_COLUMN_WIDTH) {
    return {
      minColumnWidth: PRODUCT_CARD_COLUMN_MIN,
      maxColumnWidth: PRODUCT_CARD_COLUMN_MAX,
    };
  }
  return { minColumnWidth: width };
}

/** A product-card row draws one row of placeholders. Other grids keep the request limit. */
const PRODUCT_CARD_SKELETON_COUNT = 4;

function gridLoadingCount(
  minColumnWidth: string | undefined,
  placeholderCount: number,
): number {
  if (minColumnWidth?.trim() === PRODUCT_CARD_COLUMN_WIDTH) {
    return PRODUCT_CARD_SKELETON_COUNT;
  }
  return placeholderCount;
}

export const DataRepeater: React.FC<DataRepeaterProps> = ({
  id: nodeId,
  source: sourceName,
  iteratorPath,
  pageSize,
  paginationMode,
  paginationTotal,
  paginationLimit,
  paginationFormKey,
  paginationPosition,
  paginationAlign,
  resetWhenFormKeysChange,
  limit,
  viewMoreOnExhausted,
  filter = false,
  filterStateKey = 'activeFilters',
  filterPath = 'institutionalStats',
  sortable = false,
  sortOptions,
  layout = 'flex',
  direction = 'column',
  wrap = 'nowrap',
  align = 'stretch',
  justify = 'start',
  columns = 1,
  columnsMd,
  columnsLg,
  columnsXl,
  minColumnWidth,
  gap = 'sm',
  emptyStateText,
  errorStateHeading,
  errorStateBody,
  errorCtaLabel,
  onRetry,
  adapterError,
  children,
  repeatInsideShell = false,
  loadingVariant = 'rows',
}) => {
  const dsm = useDataSourceManager();
  const { formState } = dsm;
  const tracking = useTracking();
  const itemIndexPath = useItemScopeIndexPath();
  const source = dsm.getSource(sourceName);
  const sourceData = source?.data;
  const status = source?.status;
  const requestInterpolationContext = useMemo(() => {
    const sourceValues: Record<string, unknown> = {};
    for (const [name, entry] of Object.entries(dsm.sources)) {
      sourceValues[name] = entry.data;
    }
    return {
      ...sourceValues,
      context: dsm.flowContext,
      formState,
    };
  }, [dsm.sources, dsm.flowContext, formState]);
  const requestedItemLimit = useMemo(
    () =>
      resolveRequestedItemLimit(
        dsm.getDeclaration?.(sourceName),
        requestInterpolationContext,
      ),
    [dsm, sourceName, requestInterpolationContext],
  );
  const placeholderCount = requestedItemLimit.count;

  const allItems = useMemo(
    () => extractIteratorArray(sourceData, iteratorPath) ?? [],
    [sourceData, iteratorPath],
  );

  // Stable, remount-surviving key for the View More reveal flag. Scoped
  // by the host through `composeDedupKey` — the same scoping the
  // binding-enhancer's `stableKey` uses — so two flows (or two
  // dashboard hosts) mounted on the same page, and a forward/back
  // revisit, don't cross-contaminate. The `itemIndexPath` segment is
  // required for the same reason `stableKey` includes it: a
  // `platform.dataRepeater` nested inside an outer repeater's row
  // template renders the SAME Puck node id once per outer row, so
  // without the segment every nested instance would collapse onto one
  // shared reveal flag — expanding one row's nested list would expand
  // (or collapse) them all. `undefined` when no Puck node id is
  // present (ad-hoc / legacy mounts), in which case reveal state falls
  // back to plain per-instance `useState` (pre-fix behaviour
  // preserved).
  const scopedNodeId =
    typeof nodeId === 'string' && nodeId.length > 0
      ? itemIndexPath !== undefined
        ? `${nodeId}::${itemIndexPath}`
        : nodeId
      : undefined;
  const revealKey =
    scopedNodeId === undefined
      ? undefined
      : tracking
        ? tracking.composeDedupKey(scopedNodeId)
        : composeFlowElementKey({ nodeId: scopedNodeId });

  // Client-side tag re-ranking (before sort + pagination so the best
  // matches surface within the View More window). Rows matching more
  // active filter keys float up; nothing is dropped. Reading the raw
  // form-state string as the dependency keeps the memo stable across
  // unrelated form-state changes — only this key re-ranks.
  const activeFilterValue = filter ? formState[filterStateKey] : undefined;
  const items = useMemo(() => {
    if (!filter) return allItems;
    const activeKeys = parseActiveFilterKeys(activeFilterValue);
    return rankItemsByTags(allItems, activeKeys, filterPath);
  }, [filter, allItems, activeFilterValue, filterPath]);

  const effectiveSortOptions =
    sortable && sortOptions && sortOptions.length > 0
      ? sortOptions
      : EMPTY_OPTIONS;
  const { sortedItems, activeSortIndex, canSort, setActiveSortIndex } = useSort(
    items,
    effectiveSortOptions,
  );

  // Align with the admin editor: `'none'` / omitted means no footer.
  // `viewMore` owns its own limit/expand windowing, so `usePagination`
  // is disabled (pageSize -> undefined) in that mode and for `'none'`.
  const effectiveMode = resolvePaginationMode(paginationMode, pageSize);
  const isViewMore = effectiveMode === 'viewMore';
  const isNone = effectiveMode === 'none';
  const isServer = effectiveMode === 'server';
  const standardMode: PaginationMode =
    effectiveMode === 'loadMore' ? 'loadMore' : 'paginated';
  const {
    visibleItems: pagedItems,
    page,
    totalPages,
    hasMore,
    goToPage,
    loadMore,
  } = usePagination(
    sortedItems,
    isViewMore || isNone || isServer ? undefined : pageSize,
    {
      mode: standardMode,
    },
  );

  const serverPage = useMemo(
    () =>
      isServer
        ? resolveServerPagination({
            sourceData,
            totalPath: paginationTotal,
            limit: paginationLimit,
          })
        : undefined,
    [isServer, sourceData, paginationTotal, paginationLimit],
  );

  const formKeys = useMemo(
    () => paginationFormKeys(paginationFormKey),
    [paginationFormKey],
  );
  // Offset is 0 until a click writes a page, so the highlight follows
  // that written page and otherwise stays on page 1.
  const selectedPage = (() => {
    if (!serverPage) return 1;
    const written = formKeys
      ? Number(formState[formKeys.currentPage])
      : Number.NaN;
    if (
      Number.isInteger(written) &&
      written >= 1 &&
      written <= serverPage.totalPages
    ) {
      return written;
    }
    return serverPage.currentPage;
  })();
  const resetKeys = useMemo(
    () => readResetFormKeys(resetWhenFormKeysChange),
    [resetWhenFormKeysChange],
  );
  const resetSignature = resetKeys
    .map((key) => {
      const value = formState[key];
      if (typeof value === 'string') return value;
      if (Array.isArray(value)) return value.join(',');
      return '';
    })
    .join('\u0000');
  const resetSignatureRef = useRef<string | undefined>(undefined);

  const publishFormEntries = useCallback(
    (entries: Record<string, string> | undefined) => {
      if (!entries) return;
      const changed = Object.entries(entries).filter(
        ([key, value]) => formState[key] !== value,
      );
      if (changed.length === 0) return;
      const next = Object.fromEntries(changed);
      if (dsm.updateFormStateMany) {
        dsm.updateFormStateMany(next);
        return;
      }
      for (const [key, value] of changed) {
        dsm.updateFormState(key, value);
      }
    },
    [dsm, formState],
  );

  useEffect(() => {
    if (!isServer || !formKeys) return;
    if (resetSignatureRef.current === undefined) {
      resetSignatureRef.current = resetSignature;
      return;
    }
    if (resetSignatureRef.current === resetSignature) return;
    resetSignatureRef.current = resetSignature;
    const offsetIsFirst = (formState[formKeys.offset] ?? '0') === '0';
    const pageIsFirst = (formState[formKeys.currentPage] ?? '1') === '1';
    if (offsetIsFirst && pageIsFirst) return;
    publishFormEntries({
      [formKeys.offset]: '0',
      [formKeys.currentPage]: '1',
    });
  }, [isServer, formKeys, resetSignature, formState, publishFormEntries]);

  const handleServerPage = useCallback(
    (nextPage: number) => {
      if (!serverPage || !formKeys) return;
      const nextOffset = (nextPage - 1) * serverPage.limit;
      publishFormEntries(
        paginationFormStateEntries(paginationFormKey, {
          ...serverPage,
          offset: nextOffset,
          currentPage: nextPage,
        }),
      );
    },
    [serverPage, formKeys, paginationFormKey, publishFormEntries],
  );

  const dispatcher = useActionDispatcher();

  // Initialise from the remount-surviving store so a subtree teardown +
  // re-create (DSM formState/sources churn — e.g. toggling a filter
  // pill) restores an already-expanded window instead of collapsing it.
  // Gated on `itemsRef === allItems`: if the source re-fetched while the
  // component was unmounted the stored reference is stale and we start
  // collapsed.
  const [expanded, setExpanded] = useState<boolean>(() => {
    if (!revealKey) return false;
    const entry = VIEW_MORE_REVEAL.get(revealKey);
    return entry !== undefined && entry.itemsRef === allItems
      ? entry.expanded
      : false;
  });

  // Collapse the window ONLY on a genuine source re-fetch (new
  // `allItems` reference). `lastRevealItemsRef` is seeded with the
  // current reference on mount, so a remount that restored `expanded`
  // from the store is never clobbered, and client-side filter
  // re-ranking (which leaves `allItems` untouched) never resets here.
  const lastRevealItemsRef = useRef<unknown>(allItems);
  useEffect(() => {
    if (lastRevealItemsRef.current === allItems) return;
    lastRevealItemsRef.current = allItems;
    setExpanded(false);
  }, [allItems]);

  // Mirror the reveal flag into the module-level store on every change
  // so the next remount restores it. Keyed by the stable id; no-op when
  // the repeater has no Puck node id (legacy/ad-hoc mounts).
  useEffect(() => {
    if (!revealKey) return;
    VIEW_MORE_REVEAL.set(revealKey, { itemsRef: allItems, expanded });
  }, [revealKey, allItems, expanded]);

  const viewMoreLimit =
    isViewMore && typeof limit === 'number' && limit > 0 ? limit : undefined;

  // The conditional gate: are there genuinely more rows than the limit?
  // (limit=3 but 2 results -> already at the cap -> no hidden rows).
  const hasHiddenRows =
    isViewMore &&
    !expanded &&
    viewMoreLimit !== undefined &&
    sortedItems.length > viewMoreLimit;
  const onExhausted = resolveViewMoreOnExhausted(viewMoreOnExhausted);
  const showViewMoreCta = hasHiddenRows || onExhausted === 'advance';

  const visibleItems = useMemo(() => {
    if (!isViewMore) return pagedItems;
    if (expanded || viewMoreLimit === undefined) return sortedItems;
    return sortedItems.slice(0, viewMoreLimit);
  }, [isViewMore, pagedItems, expanded, viewMoreLimit, sortedItems]);

  const handleViewMoreClick = useCallback(() => {
    // More rows hidden -> reveal the rest of the response.
    if (hasHiddenRows) {
      setExpanded(true);
      return;
    }
    // Everything already shown (or never more than `limit`) -> advance
    // the flow, mirroring the legacy EL "View More Programs" behavior.
    dispatcher.dispatch(VIEW_MORE_FORWARD_ACTION);
  }, [hasHiddenRows, dispatcher]);

  const liveRegionId = useId();
  const sortLabelId = useId();

  // Offset the per-page local index back to its position in the full
  // (sorted) collection so each row's identity is absolute, not relative
  // to the current window. In `paginated` mode page N row 0 would
  // otherwise collapse onto page 0 row 0 — sharing a Trackable
  // `stableKey` suffix and suppressing later-page product events — and
  // would also report the wrong 1-based product `position`. `loadMore`
  // and the disabled path always start at page 0, so the offset is 0.
  // Server mode's resolved page always reports offset 0. The page on
  // screen is `selectedPage`, so row identity follows that page.
  const indexOffset = isServer
    ? serverPage
      ? (selectedPage - 1) * serverPage.limit
      : 0
    : pageSize && pageSize > 0
      ? page * pageSize
      : 0;

  // Section/bucket (from `iteratorPath`) threaded to each row to refine the
  // card `location` per results-page section.
  const section = sectionFromIteratorPath(iteratorPath);

  const isShell = repeatInsideShell === true;
  const renderedItems = isShell
    ? []
    : visibleItems.map((row, i) => {
        const absoluteIndex = indexOffset + i;
        return (
          <RepeaterRow
            key={deriveRowKey(row, absoluteIndex)}
            item={row}
            index={absoluteIndex}
            section={section}
          >
            {children}
          </RepeaterRow>
        );
      });
  const repeatedShell = isShell
    ? repeatInsideShellChild(children, visibleItems, indexOffset, section)
    : null;
  const gridColumnProps = {
    columns,
    ...(columnsMd ? { 'columns-md': columnsMd } : {}),
    ...(columnsLg ? { 'columns-lg': columnsLg } : {}),
    ...(columnsXl ? { 'columns-xl': columnsXl } : {}),
    ...scrollingColumnProps(minColumnWidth),
  };
  const gridPlaceholderCount = gridLoadingCount(
    minColumnWidth,
    placeholderCount,
  );
  const loadingCards = Array.from(
    {
      length: layout === 'grid' ? gridPlaceholderCount : placeholderCount,
    },
    (_, index) => <LoadingCardSkeleton key={`loading-${index}`} />,
  );
  const cardLoadingContent = isShell ? (
    loadingInsideShellChild(children, placeholderCount)
  ) : layout === 'grid' ? (
    <Grid
      data-layout='grid'
      className='items-stretch'
      {...gridColumnProps}
      gap={gap}
    >
      {loadingCards}
    </Grid>
  ) : (
    <Flex
      data-layout='flex'
      direction={direction}
      wrap={wrap}
      align={align}
      justify={justify}
      gap={gap}
    >
      {loadingCards}
    </Flex>
  );
  const loadingState =
    loadingVariant === 'card' ? (
      <LoadingSkeleton rows={placeholderCount}>
        {cardLoadingContent}
      </LoadingSkeleton>
    ) : (
      <LoadingSkeleton />
    );

  const showPagerAbove =
    paginationPosition === 'top' || paginationPosition === 'both';
  const showPagerBelow = paginationPosition !== 'top';
  const pagerAlignClass =
    paginationAlign === 'left'
      ? 'justify-start'
      : paginationAlign === 'right'
        ? 'justify-end'
        : 'justify-center';
  const serverPagination = (placement: 'top' | 'bottom'): React.ReactNode =>
    isServer && serverPage && formKeys && serverPage.totalPages > 1 ? (
      <div className='my-4'>
        <Pagination
          mode='client'
          className={pagerAlignClass}
          currentPage={selectedPage}
          totalPages={serverPage.totalPages}
          onPageChange={handleServerPage}
          previousLabel=''
          nextLabel=''
          aria-label={
            paginationPosition === 'both'
              ? placement === 'top'
                ? 'Pagination, top'
                : 'Pagination, bottom'
              : 'pagination'
          }
        />
      </div>
    ) : null;

  return (
    <BoundStatusShell
      status={status}
      bound
      adapterError={adapterError}
      // The repeater's own narrowing, not the manager's: rows can survive
      // extraction and still be excluded by a filter or fall outside the
      // current page.
      narrowedToNothing={visibleItems.length === 0}
      slots={{
        loading: loadingState,
        error: (
          <ErrorState
            heading={errorStateHeading}
            body={errorStateBody}
            ctaLabel={errorCtaLabel}
            onRetry={onRetry}
          />
        ),
        // Both reasons render the same copy, and that is a choice about this
        // component rather than a claim that they mean the same thing: the
        // text is authored, so whoever configures the repeater already owns
        // the wording and has one string to give.
        //
        // The two do differ in cause here -- rows can exist and still be
        // narrowed away by extraction, filtering, or paging, which reports
        // `narrowed` where a source with nothing in it reports `source-empty`.
        // They coincide only in the row count being zero. A consumer that wants
        // them worded differently takes the reason from `resolveBoundStatus`,
        // as the dashboard's chart frame does.
        empty: <EmptyState body={emptyStateText} />,
      }}
    >
      <div data-testid='data-repeater'>
        {showPagerAbove ? serverPagination('top') : null}
        {canSort ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: 12,
            }}
          >
            <label id={sortLabelId} style={{ marginRight: 8 }}>
              Sort by
            </label>
            <select
              aria-labelledby={sortLabelId}
              value={activeSortIndex}
              onChange={(e) => setActiveSortIndex(Number(e.target.value))}
            >
              {effectiveSortOptions.map((opt, i) => (
                <option key={`${opt.field}-${opt.direction}-${i}`} value={i}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {isShell ? (
          <div
            role='region'
            aria-live='polite'
            aria-label='Results'
            id={liveRegionId}
          >
            {repeatedShell}
          </div>
        ) : layout === 'grid' ? (
          <Grid
            role='region'
            aria-live='polite'
            aria-label='Results'
            id={liveRegionId}
            data-layout='grid'
            // Stretch cells so cards in a row share the tallest card's height
            // (equal-height grid). This is the CSS grid default; set explicitly
            // so the contract survives future `align` changes.
            className='items-stretch'
            {...gridColumnProps}
            gap={gap}
          >
            {renderedItems}
          </Grid>
        ) : (
          <Flex
            role='region'
            aria-live='polite'
            aria-label='Results'
            id={liveRegionId}
            data-layout='flex'
            direction={direction}
            wrap={wrap}
            align={align}
            justify={justify}
            gap={gap}
          >
            {renderedItems}
          </Flex>
        )}

        {isServer ? (
          showPagerBelow ? (
            serverPagination('bottom')
          ) : null
        ) : isViewMore ? (
          showViewMoreCta ? (
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <Button
                variant='primaryFill'
                onClick={handleViewMoreClick}
                data-testid='data-repeater-view-more'
              >
                {VIEW_MORE_LABEL}
              </Button>
            </div>
          ) : null
        ) : !isNone && pageSize && pageSize > 0 ? (
          effectiveMode === 'loadMore' ? (
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              {hasMore ? (
                <button type='button' onClick={loadMore}>
                  Load more
                </button>
              ) : null}
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: 16,
              }}
            >
              <button
                type='button'
                onClick={() => goToPage(page - 1)}
                disabled={page === 0}
              >
                Previous
              </button>
              <span aria-live='polite'>
                Page {page + 1} of {Math.max(totalPages, 1)}
              </span>
              <button
                type='button'
                onClick={() => goToPage(page + 1)}
                disabled={!hasMore}
              >
                Next
              </button>
            </div>
          )
        ) : null}
      </div>
    </BoundStatusShell>
  );
};
